import { useEffect, useState, useRef } from "react";
import {
  Search,
  ShoppingCart,
  MapPin,
  Menu,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  User,
  Heart,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { getLanguage, setLanguage } from "../../../utils/Helper";
import ProductsApiService from "../../../services/ProductsApiService";
import { useCart } from "../../../context/CartContext";
import "./AmazonNavbar.scss";
import { getUserLocationByLatAndLong } from "../../../services/UsersApiService";
import { useWishlist } from "../../../context/WishlistContext";
import SocialLinks from "../../general/social-links/SocialLinks";

const AmazonNavbar = ({ isHomePage }) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(getLanguage());
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [userLocation, setUserLocation] = useState({
    city: "",
    loading: true,
  });
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [hasMoreResults, setHasMoreResults] = useState(false);

  // ── NEW: nav-links scroll state ──────────────────────────────────
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const navLinksRef = useRef(null);
  // ────────────────────────────────────────────────────────────────

  const authContext = useAuth();
  const [navData, setNavData] = useState([]);
  const navigate = useNavigate();
  const { cartCount, toggleCart } = useCart();
  const { wishlistCount } = useWishlist();

  const searchTimeoutRef = useRef(null);
  const searchDropdownRef = useRef(null);
  const dropdownOpenTimeoutRef = useRef(null);
  const dropdownCloseTimeoutRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [isScrolled, setIsScrolled] = useState(isHomePage ? false : true);

  // ── NEW: check scroll arrows visibility ─────────────────────────
  const checkScrollability = () => {
    const el = navLinksRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  const scrollNavLinks = (direction) => {
    const el = navLinksRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === "right" ? 200 : -200,
      behavior: "smooth",
    });
  };
  // ────────────────────────────────────────────────────────────────

  // Get user's actual location
  const getUserLocation = async () => {
    setUserLocation({ city: "Cairo", loading: true });

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;

          try {
            const response = await getUserLocationByLatAndLong(
              latitude,
              longitude,
            );
            const data = response.data;

            const city =
              data.address?.city ||
              data.address?.town ||
              data.address?.village ||
              data.address?.state ||
              "Cairo";

            if (data?.address?.country && data?.address?.country !== "Egypt") {
              setUserLocation({ city: "Out of zone", loading: false });
              return;
            }

            setUserLocation({ city, loading: false });
            localStorage.setItem("userLocation", city);
          } catch (error) {
            console.error("Error getting location name:", error);
            setUserLocation({ city: "Cairo", loading: false });
          }
        },
        (error) => {
          console.error("Geolocation error:", error);
          const savedLocation = localStorage.getItem("userLocation");
          setUserLocation({
            city: savedLocation || "Cairo",
            loading: false,
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        },
      );
    } else {
      const savedLocation = localStorage.getItem("userLocation");
      setUserLocation({
        city: savedLocation || "Cairo",
        loading: false,
      });
    }
  };

  const cutWords = (text, maxLength) => {
    if (!text) return "";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  const handleCollectionPath = (
    collectionName,
    categoryName = null,
    subCategory = null,
    brand = null,
  ) => {
    let path = `/collection/${collectionName}`;
    path += categoryName ? `/${categoryName}` : "";
    path += subCategory ? `?sub_cat=${subCategory}` : "";
    path += brand ? `?brand=${brand}` : "";
    return path.toString().toLowerCase().trim().split(" ").join("-");
  };

  const handleLanguage = () => {
    const newLang = currentLanguage === "EN" ? "AR" : "EN";
    setLanguage(newLang);
    setCurrentLanguage(newLang);
    window.location.reload();
  };

  const handleProductClick = (currentProduct) => {
    const collectionName = currentProduct.collectionNameEn
      .toString()
      .toLowerCase()
      .trim()
      .split(" ")
      .join("-");
    const catName = currentProduct.categoryNameEn
      .toString()
      .toLowerCase()
      .trim()
      .split(" ")
      .join("-");
    const subCatName = !currentProduct.subCategoryNameEn
      ? null
      : currentProduct.subCategoryNameEn
          .toString()
          .toLowerCase()
          .trim()
          .split(" ")
          .join("-");
    return subCatName
      ? `/collection/${collectionName}/${catName}/${subCatName}/${currentProduct.id}`
      : `/collection/${collectionName}/${catName}/${currentProduct.id}`;
  };

  const handleLogout = () => {
    authContext.logout();
    setShowProfileDropdown(false);
    window.location.href = "/";
  };

  const handleViewMoreResults = () => {
    setCurrentPage((prev) => prev + 1);
  };

  const handleNavMouseEnter = (index) => {
    if (dropdownCloseTimeoutRef.current) {
      clearTimeout(dropdownCloseTimeoutRef.current);
      dropdownCloseTimeoutRef.current = null;
    }

    if (dropdownOpenTimeoutRef.current) {
      clearTimeout(dropdownOpenTimeoutRef.current);
    }

    dropdownOpenTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(index);
    }, 200);
  };

  const handleNavMouseLeave = () => {
    if (dropdownOpenTimeoutRef.current) {
      clearTimeout(dropdownOpenTimeoutRef.current);
      dropdownOpenTimeoutRef.current = null;
    }

    dropdownCloseTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 500);
  };

  const handleMegaMenuMouseEnter = () => {
    if (dropdownCloseTimeoutRef.current) {
      clearTimeout(dropdownCloseTimeoutRef.current);
      dropdownCloseTimeoutRef.current = null;
    }
  };

  const handleMegaMenuMouseLeave = () => {
    dropdownCloseTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 300);
  };

  // Search functionality
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim().length > 0) {
      setIsSearching(true);

      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const filters = {
            search: searchQuery.trim(),
            categoryId: 0,
            subCategoryId: 0,
            brandId: 0,
            collectionId: 0,
          };

          const response = await ProductsApiService.getProductsList(
            6,
            currentPage,
            filters,
          );
          if (currentPage === 0) {
            setSearchResults(response.products);
          } else {
            setSearchResults((prevResults) => [
              ...prevResults,
              ...response.products,
            ]);
          }
          setHasMoreResults(response.products.length === 6);
          setShowSearchResults(true);
        } catch (error) {
          console.error("Search error:", error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 500);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, currentPage]);

  // Handle click outside search
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchDropdownRef.current &&
        !searchDropdownRef.current.contains(event.target)
      ) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (authContext?.systemCollections?.length > 0) {
      setNavData(authContext.systemCollections);
    }
  }, [authContext?.systemCollections]);

  // Load location on mount
  useEffect(() => {
    getUserLocation();
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (dropdownOpenTimeoutRef.current) {
        clearTimeout(dropdownOpenTimeoutRef.current);
      }
      if (dropdownCloseTimeoutRef.current) {
        clearTimeout(dropdownCloseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isHomePage) {
      setIsScrolled(true);
      return;
    }

    const TRIGGER = window.innerHeight;

    const handleScroll = () => {
      setIsScrolled(window.scrollY > TRIGGER);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomePage]);

  // ── NEW: attach scroll listener to nav-links & re-check on navData change ──
  useEffect(() => {
    const el = navLinksRef.current;
    if (!el) return;

    checkScrollability();
    el.addEventListener("scroll", checkScrollability, { passive: true });

    const ro = new ResizeObserver(checkScrollability);
    ro.observe(el);

    return () => {
      el.removeEventListener("scroll", checkScrollability);
      ro.disconnect();
    };
  }, [navData]); // re-run when links are loaded
  // ─────────────────────────────────────────────────────────────────

  return (
    <div className={`amazon-navbar${isScrolled ? " scrolled" : ""}`}>
      {/* Top Header */}
      <div className="navbar-header">
        <div className="navbar-container">
          {/* Logo */}
          <Link to="/" className="navbar-logo pl-0">
            <img
              src="/assets/login-form-logo.png"
              alt="Addmix Store"
              className="logo-image"
            />
          </Link>

          {/* Delivery Location */}
          <div
            className="navbar-location"
            onClick={getUserLocation}
            title="Click to update location"
          >
            <MapPin size={20} />
            <div className="location-text">
              <span className="location-label">
                {userLocation.city !== "Out of zone" &&
                  (currentLanguage === "EN" ? "Deliver to" : "التوصيل إلى")}
              </span>
              <span className="location-name">
                {userLocation.loading ? (
                  <span className="location-loading">...</span>
                ) : (
                  userLocation.city
                )}
              </span>
            </div>
          </div>

          {/* Search Bar */}
          <div className="navbar-search" ref={searchDropdownRef}>
            <div className="search-wrapper">
              <input
                type="text"
                className="search-input"
                placeholder={
                  currentLanguage === "EN"
                    ? "Search products..."
                    : "ابحث عن المنتجات..."
                }
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(0);
                }}
              />
              <button className="search-button">
                <Search size={20} />
              </button>
            </div>

            {/* Search Results Dropdown */}
            {showSearchResults && searchQuery.trim().length > 0 && (
              <div className="search-results">
                {isSearching && currentPage === 0 ? (
                  <div className="search-loading">
                    <div className="spinner"></div>
                    <p>Searching...</p>
                  </div>
                ) : searchResults.length > 0 ? (
                  <>
                    <div className="search-results-list">
                      {searchResults.map((product) => (
                        <Link
                          key={product.id}
                          to={handleProductClick(product)}
                          className="search-result-item"
                          onClick={() => {
                            setShowSearchResults(false);
                            setSearchQuery("");
                          }}
                        >
                          <img
                            src={
                              product.imageUrl ||
                              "/assets/images/addmix/lock.png"
                            }
                            alt={
                              currentLanguage === "EN"
                                ? product.nameEn
                                : product.nameAr
                            }
                            className="result-image"
                          />
                          <div className="result-info">
                            <p className="result-name">
                              {cutWords(
                                currentLanguage === "EN"
                                  ? product.nameEn
                                  : product.nameAr,
                                50,
                              )}
                            </p>
                            <div className="result-price">
                              {product.discountPrice !== product.price ? (
                                <>
                                  <span className="price-new">
                                    EGP {product.discountPrice}
                                  </span>
                                  <span className="price-old">
                                    EGP {product.price}
                                  </span>
                                </>
                              ) : (
                                <span className="price-regular">
                                  EGP {product.price}
                                </span>
                              )}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>

                    {hasMoreResults && (
                      <div className="search-footer">
                        <button
                          className="view-more-btn"
                          onClick={handleViewMoreResults}
                          disabled={isSearching}
                        >
                          {isSearching ? (
                            <>
                              <div className="spinner-small"></div>
                              <span>Loading...</span>
                            </>
                          ) : currentLanguage === "EN" ? (
                            "View More Results"
                          ) : (
                            "عرض المزيد"
                          )}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="search-empty">
                    <p>
                      {currentLanguage === "EN"
                        ? "No products found"
                        : "لا توجد نتيجة للبحث"}
                    </p>
                  </div>
                )}
              </div>
            )}
            <div className="social-links d-md-none mt-3 mb-1">
              <SocialLinks />
            </div>
          </div>

          {/* Right Section */}
          <div className="navbar-actions btn pl-0">
            <button
              className="navbar-action language-action btn"
              onClick={handleLanguage}
            >
              {currentLanguage === "EN" ? (
                <img
                  src="https://flagcdn.com/w40/us.png"
                  alt="English"
                  className="flag-icon"
                />
              ) : (
                <img
                  src="https://flagcdn.com/w40/eg.png"
                  alt="العربية"
                  className="flag-icon"
                />
              )}
              <span className="action-title">{currentLanguage}</span>
            </button>

            <div
              className="navbar-action account-section btn"
              onMouseEnter={() => setShowProfileDropdown(true)}
              onMouseLeave={() => setShowProfileDropdown(false)}
            >
              <Link
                to={authContext?.user ? "/profile" : "/auth"}
                className="account-link"
              >
                <span className="account-greeting">
                  {currentLanguage === "EN" ? "Hello, " : "مرحباً، "}
                  {authContext?.username ||
                    (currentLanguage === "EN" ? "sign in" : "تسجيل الدخول")}
                </span>
                <span className="account-title">
                  {currentLanguage === "EN"
                    ? "Account & Lists"
                    : "الحساب والقوائم"}
                  <ChevronDown size={14} />
                </span>
              </Link>

              {showProfileDropdown && authContext?.user && (
                <div className="profile-dropdown">
                  <Link to="/profile" className="dropdown-item">
                    <User size={16} />
                    <span>
                      {currentLanguage === "EN" ? "My Profile" : "حسابي"}
                    </span>
                  </Link>
                  <button
                    className="dropdown-item logout-btn"
                    onClick={handleLogout}
                  >
                    <span>
                      {currentLanguage === "EN" ? "Logout" : "تسجيل خروج"}
                    </span>
                  </button>
                </div>
              )}
            </div>

            <Link to="/wishlist" className="navbar-action wishlist-action btn">
              <div className="wishlist-icon-wrapper">
                <Heart size={24} />
                {wishlistCount > 0 && (
                  <span className="wishlist-count">{wishlistCount}</span>
                )}
              </div>
              <span className="action-title">
                {currentLanguage === "EN" ? "Wishlist" : "المفضلة"}
              </span>
            </Link>

            <button className="navbar-cart btn" onClick={toggleCart}>
              <div className="cart-icon-wrapper">
                <ShoppingCart size={32} />
                {cartCount > 0 && (
                  <span className="cart-count">{cartCount}</span>
                )}
              </div>
              <span className="cart-label">
                {currentLanguage === "EN" ? "Cart" : "السلة"}
              </span>
            </button>

            <button
              className="mobile-header-btn btn d-md-none"
              onClick={handleLanguage}
            >
              {currentLanguage === "EN" ? (
                <img
                  src="https://flagcdn.com/w40/us.png"
                  alt="English"
                  className="flag-icon-mobile"
                />
              ) : (
                <img
                  src="https://flagcdn.com/w40/eg.png"
                  alt="العربية"
                  className="flag-icon-mobile"
                />
              )}
            </button>
          </div>

          <button
            className="mobile-menu-toggle btn"
            onClick={() => setIsMenuOpen(true)}
          >
            <Menu size={24} />
          </button>
        </div>
      </div>

      {/* Navigation Bar */}
      <div className="navbar-nav">
        <div className="navbar-container d-md-flex d-none">
          {/* ── MODIFIED: nav-links wrapped in scroll container ── */}
          <div className="nav-scroll-container">
            {/* Left Arrow */}
            <button
              className={`nav-scroll-arrow nav-scroll-arrow--left${canScrollLeft ? " visible" : ""}`}
              onClick={() => scrollNavLinks("left")}
              aria-label="Scroll left"
            >
              <ChevronLeft size={18} />
            </button>

            {/* Left fade mask */}
            <div
              className={`nav-fade nav-fade--left${canScrollLeft ? " visible" : ""}`}
            />

            <div
              className="nav-links"
              ref={navLinksRef}
              onScroll={checkScrollability}
            >
              {navData.map((collection, index) => (
                <div
                  key={index}
                  className="nav-item"
                  onMouseEnter={() => handleNavMouseEnter(index)}
                  onMouseLeave={handleNavMouseLeave}
                >
                  <Link
                    to={handleCollectionPath(collection.name_url)}
                    className="nav-link"
                  >
                    {collection.name}
                  </Link>

                  {collection?.categories.length > 0 &&
                    activeDropdown === index && (
                      <div
                        className="mega-menu"
                        onMouseEnter={handleMegaMenuMouseEnter}
                        onMouseLeave={handleMegaMenuMouseLeave}
                      >
                        <div className="mega-menu-content">
                          <div className="categories-section">
                            {collection?.categories.map(
                              (category, catIndex) => (
                                <div key={catIndex} className="category-column">
                                  <Link
                                    to={handleCollectionPath(
                                      collection.name_url,
                                      category.name_url,
                                    )}
                                    className="category-title"
                                  >
                                    {category.name}
                                  </Link>
                                  <div className="subcategory-list">
                                    {category?.sub_categories.map(
                                      (subItem, subIndex) => (
                                        <Link
                                          key={subIndex}
                                          to={handleCollectionPath(
                                            collection.name_url,
                                            category.name_url,
                                            subItem.name_url,
                                          )}
                                          className="subcategory-link"
                                        >
                                          {subItem.name}
                                        </Link>
                                      ),
                                    )}
                                  </div>
                                </div>
                              ),
                            )}
                          </div>

                          {collection.top_brands?.length > 0 && (
                            <div className="brands-section">
                              <h4 className="brands-title">
                                {currentLanguage === "EN"
                                  ? "TOP BRANDS"
                                  : "أفضل العلامات التجارية"}
                              </h4>
                              <div className="brands-grid">
                                {collection.top_brands.map(
                                  (brand, brandIndex) => (
                                    <div
                                      key={brandIndex}
                                      className="brand-item"
                                      onClick={() => {
                                        navigate(
                                          handleCollectionPath(
                                            collection.name_url,
                                            null,
                                            null,
                                            brand.name_url,
                                          ),
                                        );
                                      }}
                                    >
                                      <img
                                        src={brand.image_url}
                                        alt={brand.name}
                                        className="brand-logo"
                                        onError={(e) => {
                                          e.target.style.display = "none";
                                        }}
                                      />
                                      <span className="brand-name">
                                        {brand.name}
                                      </span>
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                </div>
              ))}

              <Link to="/about-us" className="nav-link">
                {currentLanguage === "EN" ? "About Us" : "من نحن"}
              </Link>
              <Link to="/privacy-policy" className="nav-link">
                {currentLanguage === "EN" ? "Privacy Policy" : "سياسة الخصوصية"}
              </Link>
              <Link to="/contact-us" className="nav-link">
                {currentLanguage === "EN" ? "Contact Us" : "اتصل بنا"}
              </Link>
            </div>

            {/* Right fade mask */}
            <div
              className={`nav-fade nav-fade--right${canScrollRight ? " visible" : ""}`}
            />

            {/* Right Arrow */}
            <button
              className={`nav-scroll-arrow nav-scroll-arrow--right${canScrollRight ? " visible" : ""}`}
              onClick={() => scrollNavLinks("right")}
              aria-label="Scroll right"
            >
              <ChevronRight size={18} />
            </button>
          </div>
          {/* ── END MODIFIED SECTION ── */}

          <div className="social-links">
            <SocialLinks />
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Menu */}
      {isMenuOpen && (
        <div className="mobile-sidebar">
          <div
            className="sidebar-overlay"
            onClick={() => setIsMenuOpen(false)}
          ></div>
          <div className="sidebar-content">
            <div className="sidebar-header">
              <div className="sidebar-user-info">
                <User size={24} />
                <div className="sidebar-greeting">
                  <span>
                    {currentLanguage === "EN" ? "Hello, " : "مرحباً، "}
                  </span>
                  <strong>
                    {authContext?.username ||
                      (currentLanguage === "EN" ? "Guest" : "ضيف")}
                  </strong>
                </div>
              </div>
              <button
                className="sidebar-close"
                onClick={() => setIsMenuOpen(false)}
              >
                ×
              </button>
            </div>

            {!authContext?.user ? (
              <div className="mobile-account-section">
                <Link
                  to="/auth"
                  className="signin-btn"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {currentLanguage === "EN" ? "Sign In" : "تسجيل الدخول"}
                </Link>
              </div>
            ) : (
              <div className="mobile-account-section">
                <Link
                  to="/profile"
                  className="profile-link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <User size={18} />
                  <span>
                    {currentLanguage === "EN" ? "My Account" : "حسابي"}
                  </span>
                </Link>
              </div>
            )}

            <div className="mobile-quick-actions">
              <Link
                to="/wishlist"
                className="quick-action-item"
                onClick={() => setIsMenuOpen(false)}
              >
                <div className="action-icon">
                  <Heart size={20} />
                  {wishlistCount > 0 && (
                    <span className="mini-badge">{wishlistCount}</span>
                  )}
                </div>
                <span className="action-label">
                  {currentLanguage === "EN" ? "Wishlist" : "المفضلة"}
                </span>
              </Link>

              <button className="quick-action-item" onClick={toggleCart}>
                <div className="action-icon">
                  <ShoppingCart size={20} />
                  {cartCount > 0 && (
                    <span className="mini-badge">{cartCount}</span>
                  )}
                </div>
                <span className="action-label">
                  {currentLanguage === "EN" ? "Cart" : "السلة"}
                </span>
              </button>

              <button className="quick-action-item" onClick={handleLanguage}>
                <div className="action-icon">
                  {currentLanguage === "EN" ? (
                    <img
                      src="https://flagcdn.com/w40/us.png"
                      alt="English"
                      className="flag-icon-mobile"
                    />
                  ) : (
                    <img
                      src="https://flagcdn.com/w40/eg.png"
                      alt="العربية"
                      className="flag-icon-mobile"
                    />
                  )}
                </div>
                <span className="action-label">{currentLanguage}</span>
              </button>
            </div>

            {authContext?.user && (
              <div className="mobile-profile-section">
                <button className="logout-btn" onClick={handleLogout}>
                  {currentLanguage === "EN" ? "Logout" : "تسجيل خروج"}
                </button>
              </div>
            )}

            <div className="sidebar-body">
              <div className="sidebar-section">
                <h3 className="sidebar-section-title">
                  {currentLanguage === "EN"
                    ? "Shop by Category"
                    : "تسوق حسب الفئة"}
                </h3>
                {navData.map((collection, index) => (
                  <div key={index} className="sidebar-collection">
                    <button
                      className="sidebar-collection-toggle"
                      onClick={() =>
                        setActiveDropdown(
                          activeDropdown === index ? null : index,
                        )
                      }
                    >
                      <span>{collection.name}</span>
                      <ChevronDown
                        size={20}
                        style={{
                          transform:
                            activeDropdown === index
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                          transition: "transform 0.3s ease",
                        }}
                      />
                    </button>

                    {activeDropdown === index && (
                      <div className="sidebar-dropdown">
                        {collection.categories.map((category, catIndex) => (
                          <div key={catIndex} className="sidebar-category">
                            <Link
                              to={handleCollectionPath(
                                collection.name_url,
                                category.name_url,
                              )}
                              className="sidebar-category-link"
                              onClick={() => setIsMenuOpen(false)}
                            >
                              {category.name}
                            </Link>
                            {category.sub_categories.map(
                              (subItem, subIndex) => (
                                <Link
                                  key={subIndex}
                                  to={handleCollectionPath(
                                    collection.name_url,
                                    category.name_url,
                                    subItem.name_url,
                                  )}
                                  className="sidebar-subcategory-link"
                                  onClick={() => setIsMenuOpen(false)}
                                >
                                  {subItem.name}
                                </Link>
                              ),
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                <div className="sidebar-collection">
                  <button
                    className="sidebar-collection-toggle"
                    onClick={() => {
                      setIsMenuOpen(false);
                      navigate("/about-us");
                    }}
                  >
                    {currentLanguage === "EN" ? "About Us" : "من نحن"}
                  </button>
                  <button
                    className="sidebar-collection-toggle"
                    onClick={() => {
                      setIsMenuOpen(false);
                      navigate("/privacy-policy");
                    }}
                  >
                    {currentLanguage === "EN"
                      ? "Privacy Policy"
                      : "سياسة الخصوصية"}
                  </button>
                  <button
                    className="sidebar-collection-toggle"
                    onClick={() => {
                      setIsMenuOpen(false);
                      navigate("/contact-us");
                    }}
                  >
                    {currentLanguage === "EN" ? "Contact Us" : "اتصل بنا"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmazonNavbar;
